import { JSX, useEffect, useState } from "react";
import { UseMutationResult } from "@tanstack/react-query";

import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { ButtonOwnProps, Dialog, SxProps } from "@mui/material";
import { Box, Button, Container, DialogContentText, Grid, Stack } from "@mui/material";

import conf from "../../conf";
import Spinner from "./Spinner";
import SectionHeader from "./SectionHeader";
import TermsAndConditions from "./TermsAndConditions";

type ConfirmationViewProps = {
  open: boolean;
  type: string;
  message: string | JSX.Element;
  status?: UseMutationResult["status"];
  onClose: () => void;
  onConfirm: () => void;
  onCancel?: () => void;
  onCancelLabel?: string;
  onCancelLoading?: boolean;
  requireCustomTerms?: boolean;
};
const ConfirmationView = (props: ConfirmationViewProps) => {
  const { open, type, status, message, onClose, onConfirm, onCancel, onCancelLabel } = props;
  const { requireCustomTerms } = props;

  const [agreed, setAgreed] = useState(false);

  const handleAgreed = (e) => setAgreed(e.target.checked);
  const handleConfirm = () => onConfirm();

  useEffect(() => {
    if (!open) setAgreed(false);
  }, [open]);

  useEffect(() => {
    if (status === "success") {
      setAgreed(false);
      onClose();
    }
  }, [status]);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key={`${type}-confirmation-button`}
          icon={<WarningIcon />}
          title={`Confirm: ${type.toUpperCase()}`}
          buttons={[
            <Button key={"confirmation-button-close"} color="inherit" onClick={onClose}>
              <CloseIcon />
            </Button>
          ]}
        />

        <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid container p={1} mt={1} border={1} borderRadius={1}>
                <DialogContentText>{message}</DialogContentText>
              </Grid>
            </Grid>
          </Grid>

          {requireCustomTerms ? (
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 2, mb: 2 }}>
              <TermsAndConditions type="custom" agreed={agreed} handleAgreed={handleAgreed} />

              <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end">
                <Button variant="outlined" onClick={onCancel ?? onClose}>
                  {props.onCancelLoading ? <Spinner sx={{ mx: 2 }} /> : (onCancelLabel ?? "Cancel")}
                </Button>

                <Button color="error" variant="contained" disabled={!agreed} onClick={handleConfirm}>
                  Confirm
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="flex-end" sx={{ mt: 2, mb: 2 }}>
              <Button variant="outlined" onClick={onCancel ?? onClose}>
                {props.onCancelLoading ? <Spinner sx={{ mx: 2 }} /> : (onCancelLabel ?? "Cancel")}
              </Button>

              <Button color="error" variant="contained" onClick={handleConfirm}>
                Confirm
              </Button>
            </Stack>
          )}
        </Box>
      </Container>
    </Dialog>
  );
};

type ButtonWithConfirmProps = {
  sx?: SxProps;
  type: string;
  label: string | JSX.Element;
  size?: ButtonOwnProps["size"];
  color?: ButtonOwnProps["color"];
  status?: UseMutationResult["status"];
  message: string | JSX.Element;
  variant?: ButtonOwnProps["variant"];
  disabled?: boolean;
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  onCancelLabel?: string;
  onCancelLoading?: boolean;
  fullWidth?: boolean;
  showButton?: boolean;
  defaultOpen?: boolean;
  requireCustomTerms?: boolean;
};

const ButtonWithConfirm = (props: ButtonWithConfirmProps) => {
  const {
    size = "medium",
    color = "secondary",
    variant = "contained",
    disabled = false,
    type,
    label,
    status,
    message,
    isLoading,
    onConfirm,
    onCancel,
    onCancelLabel,
    onCancelLoading,
    requireCustomTerms,
    fullWidth = false,
    showButton = true,
    defaultOpen = false,
    sx
  } = props;

  const [open, setOpen] = useState(defaultOpen);

  const onClick = () => setOpen(true);
  const onClose = () => setOpen(false);

  return (
    <div>
      {showButton && (
        <Button
          size={size}
          color={color}
          variant={variant}
          disabled={disabled}
          fullWidth={fullWidth}
          onClick={onClick}
          sx={sx}
        >
          {isLoading ? <Spinner sx={{ mx: 2 }} /> : label}
        </Button>
      )}

      <ConfirmationView
        open={open}
        type={type}
        message={message}
        status={status}
        onClose={onClose}
        onCancel={onCancel}
        onConfirm={onConfirm}
        onCancelLabel={onCancelLabel}
        onCancelLoading={onCancelLoading}
        requireCustomTerms={requireCustomTerms}
      />
    </div>
  );
};

export default ButtonWithConfirm;
